import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useInstructorAuthContext } from '../../context/InstructorAuthContext';
import { Loader2 } from 'lucide-react';

interface InstructorProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string | ReadonlyArray<string>;
  requireAll?: boolean;
  fallbackComponent?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Composant de route protégée pour les instructeurs
 * Vérifie l'authentification et les permissions avant d'afficher le contenu
 */
export const InstructorProtectedRoute: React.FC<InstructorProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requireAll = false,
  fallbackComponent,
  redirectTo = '/instructor/login'
}) => {
  const location = useLocation();
  const { 
    isAuthenticated, 
    instructor, 
    isLoading, 
    hasAnyPermission, 
    hasAllPermissions 
  } = useInstructorAuthContext();

  // Composant de chargement par défaut
  const defaultLoadingComponent = (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Vérification des permissions...</p>
      </div>
    </div>
  );

  // Afficher le chargement pendant la vérification
  if (isLoading) {
    return <>{fallbackComponent || defaultLoadingComponent}</>;
  }

  // Rediriger vers la page de connexion si non authentifié
  if (!isAuthenticated || !instructor) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Vérifier les permissions si spécifiées
  if (requiredPermissions && requiredPermissions.length > 0) {
    const permsArray: string[] = Array.isArray(requiredPermissions)
      ? [...requiredPermissions]
      : [requiredPermissions];

    let hasAccess = false;
    
    if (requireAll) {
      hasAccess = hasAllPermissions(permsArray);
    } else {
      hasAccess = hasAnyPermission(permsArray);
    }

    // Rediriger vers la page d'accès refusé si permissions insuffisantes
    if (!hasAccess) {
      return (
        <Navigate 
          to="/instructor/unauthorized" 
          state={{ 
            from: location.pathname,
            requiredPermissions: permsArray,
            requireAll 
          }} 
          replace 
        />
      );
    }
  }

  // Afficher le contenu protégé
  return <>{children}</>;
};

/**
 * HOC pour protéger les composants instructeur
 */
export const withInstructorAuth = <P extends object>(
  Component: React.ComponentType<P>,
  options: {
    requiredPermissions?: string | string[];
    requireAll?: boolean;
    fallbackComponent?: React.ReactNode;
    redirectTo?: string;
  } = {}
) => {
  const WrappedComponent: React.FC<P> = (props) => {
    return (
      <InstructorProtectedRoute {...options}>
        <Component {...props} />
      </InstructorProtectedRoute>
    );
  };

  WrappedComponent.displayName = `withInstructorAuth(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

/**
 * Composant pour afficher du contenu conditionnel basé sur les permissions
 */
interface InstructorPermissionGateProps {
  children: React.ReactNode;
  requiredPermissions: string | ReadonlyArray<string>;
  requireAll?: boolean;
  fallback?: React.ReactNode;
  showFallback?: boolean;
}

export const InstructorPermissionGate: React.FC<InstructorPermissionGateProps> = ({
  children,
  requiredPermissions,
  requireAll = false,
  fallback = null,
  showFallback = false
}) => {
  const { hasAnyPermission, hasAllPermissions, isAuthenticated, instructor } = useInstructorAuthContext();

  // Ne pas afficher si non authentifié
  if (!isAuthenticated || !instructor) {
    return showFallback ? <>{fallback}</> : null;
  }

  const permsArray: string[] = Array.isArray(requiredPermissions)
    ? [...requiredPermissions]
    : [requiredPermissions];

  let hasAccess = false;
  
  if (requireAll) {
    hasAccess = hasAllPermissions(permsArray);
  } else {
    hasAccess = hasAnyPermission(permsArray);
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return showFallback ? <>{fallback}</> : null;
};

/**
 * Hook pour vérifier l'accès à une fonctionnalité
 */
export const useInstructorAccess = (
  requiredPermissions: string | ReadonlyArray<string>,
  requireAll: boolean = false
) => {
  const { 
    isAuthenticated, 
    instructor, 
    hasAnyPermission, 
    hasAllPermissions,
    isLoading 
  } = useInstructorAuthContext();

  if (isLoading || !isAuthenticated || !instructor) {
    return {
      hasAccess: false,
      isLoading,
      isAuthenticated,
      instructor
    };
  }

  const permsArray: string[] = Array.isArray(requiredPermissions)
    ? [...requiredPermissions]
    : [requiredPermissions];

  const hasAccess = requireAll 
    ? hasAllPermissions(permsArray)
    : hasAnyPermission(permsArray);

  return {
    hasAccess,
    isLoading: false,
    isAuthenticated,
    instructor
  };
};

export default InstructorProtectedRoute;
